import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type {
  MarketComponentMarketComponentCollectionIO,
  MarketComponentMarketComponentIO,
} from "../../generated/fresh-guard-api";
import { queryKeys } from "../query-keys";

export function useMarketComponentUpdateOptimistic() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      marketComponentMarketComponentCollectionIO: MarketComponentMarketComponentCollectionIO;
    }) => {
      const res = await api.marketComponentApi.apiMarketComponentsPost(
        input.marketComponentMarketComponentCollectionIO,
      );
      return res.data;
    },
    onMutate: async ({ marketComponentMarketComponentCollectionIO }) => {
      await qc.cancelQueries({ queryKey: queryKeys.marketComponents.all });

      const previousAll = qc.getQueryData<MarketComponentMarketComponentIO[]>(
        queryKeys.marketComponents.all,
      );

      const optimisticItems =
        (marketComponentMarketComponentCollectionIO.marketComponentIOs ??
          []) as MarketComponentMarketComponentIO[];

      if (optimisticItems.length > 0) {
        qc.setQueryData<MarketComponentMarketComponentIO[]>(
          queryKeys.marketComponents.all,
          (old) => [...optimisticItems, ...(old ?? [])],
        );
      }

      return { previousAll };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousAll) {
        qc.setQueryData(queryKeys.marketComponents.all, ctx.previousAll);
      }
    },
    onSuccess: (createdCollection, _vars, ctx) => {
      const created = (createdCollection.marketComponentIOs ??
        []) as MarketComponentMarketComponentIO[];

      qc.setQueryData<MarketComponentMarketComponentIO[]>(
        queryKeys.marketComponents.all,
        (old) => {
          const list = old ?? [];

          if (!ctx?.previousAll) {
            return [...created, ...list];
          }

          const createdIds = new Set(
            created
              .map((x) => x.id)
              .filter((x): x is number => typeof x === "number"),
          );

          const withoutCreated = list.filter((x) => {
            const id = x.id;
            return typeof id !== "number" || !createdIds.has(id);
          });

          return [...created, ...withoutCreated];
        },
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.marketComponents.all });
    },
    meta: {
      skipGlobalError: false,
    },
  });
}
